/* ErrorsPanel — admin view of client-side crash reports
 *
 * Shows a grouped-by-message summary (so 47 occurrences of the same crash
 * appear as one row with a count) plus a raw feed of the latest events
 * so admins can spot new issues fast.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function ErrorsPanel() {
  const [groups, setGroups] = useState([]);
  const [items, setItems] = useState([]);
  const [unresolved, setUnresolved] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("grouped");
  // Reproduce dialog state — when an admin clicks "Reproduce", we replay the
  // failing request under the admin's session and drop the raw response into
  // this modal. Includes a confirmation step for write methods so an admin
  // can't accidentally re-issue a destructive POST/PATCH/DELETE while
  // debugging.
  const [reproTarget, setReproTarget] = useState(null); // {method, path}
  const [reproResult, setReproResult] = useState(null); // {status, body, ms}
  const [reproLoading, setReproLoading] = useState(false);

  const runReproduce = async (method, path) => {
    setReproLoading(true);
    setReproResult(null);
    const t0 = performance.now();
    try {
      const m = (method || "GET").toLowerCase();
      const resp = await api.request({
        url: path,
        method: m,
        // Don't POST a body — the goal is to see the SHAPE of the failure,
        // and most 5xx endpoints will still 500 (or produce a validation
        // error) with an empty body. Prevents accidental data mutation.
        ...(m === "get" || m === "head" ? {} : { data: {} }),
        // Never let axios reject on any status — we want to SHOW the failure
        validateStatus: () => true,
      });
      setReproResult({
        status: resp.status,
        body: resp.data,
        ms: Math.round(performance.now() - t0),
        method: m.toUpperCase(),
        path,
      });
    } catch (e) {
      setReproResult({
        status: "ERR",
        body: e?.message || String(e),
        ms: Math.round(performance.now() - t0),
        method: (method || "GET").toUpperCase(),
        path,
      });
    } finally {
      setReproLoading(false);
    }
  };

  const startReproduce = (method, path) => {
    const m = (method || "GET").toUpperCase();
    // Read-only methods run immediately. Write methods land in the confirm
    // dialog so the admin has to explicitly acknowledge before we fire.
    if (m === "GET" || m === "HEAD" || m === "OPTIONS") {
      setReproTarget({ method: m, path });
      runReproduce(m, path);
    } else {
      setReproTarget({ method: m, path, needsConfirm: true });
    }
  };

  const closeReproduce = () => {
    setReproTarget(null);
    setReproResult(null);
    setReproLoading(false);
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get("/admin/errors/summary"),
        api.get("/admin/errors?limit=100"),
      ]);
      setGroups(s.data.groups || []);
      setItems(l.data.items || []);
      setUnresolved(l.data.unresolved || 0);
    } catch { toast.error("Couldn't load errors"); }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const resolveMessage = async (message) => {
    try {
      const { data } = await api.post("/admin/errors/resolve-message", { message });
      toast.success(`Resolved ${data.resolved_count} rows`);
      refresh();
    } catch { toast.error("Couldn't resolve"); }
  };

  const resolveOne = async (error_id) => {
    try {
      await api.patch(`/admin/errors/${error_id}/resolve`, { resolved: true });
      refresh();
    } catch { toast.error("Couldn't resolve"); }
  };

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="overline mb-1">Live crash & failure log</p>
          <h2 className="font-display font-black text-2xl text-rf-ink">
            {unresolved} unresolved {unresolved === 1 ? "issue" : "issues"}
          </h2>
          <p className="text-xs text-rf-ink-soft mt-1">
            Captured automatically from the ErrorBoundary + axios interceptor (5xx + network errors).
          </p>
        </div>
        <button
          type="button"
          data-testid="errors-refresh"
          onClick={refresh}
          className="btn-outline-ink !py-2 !px-4 text-sm"
        >
          Refresh
        </button>
      </div>

      <div className="flex gap-2 mb-6">
        <button
          data-testid="errors-tab-grouped"
          onClick={() => setTab("grouped")}
          className={`px-4 py-2 rounded-full text-xs font-bold ${tab === "grouped" ? "bg-rf-ink text-rf-cream" : "bg-white text-rf-ink-soft border border-rf-ink/10"}`}
        >Grouped ({groups.length})</button>
        <button
          data-testid="errors-tab-recent"
          onClick={() => setTab("recent")}
          className={`px-4 py-2 rounded-full text-xs font-bold ${tab === "recent" ? "bg-rf-ink text-rf-cream" : "bg-white text-rf-ink-soft border border-rf-ink/10"}`}
        >Recent feed ({items.length})</button>
      </div>

      {loading && <p className="text-rf-ink-soft text-sm">Loading…</p>}

      {!loading && tab === "grouped" && (
        <div className="space-y-3">
          {groups.length === 0 && <p className="text-rf-ink-soft italic text-sm">No unresolved errors — the site is happy 🎉</p>}
          {groups.map((g) => (
            <div key={g.example_id} className="card-recipe" data-testid="errors-group">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-xs font-black uppercase tracking-wider bg-rf-orange text-white px-2 py-0.5 rounded-full">{g.count}×</span>
                    <span className="text-[10px] font-black uppercase tracking-wider text-rf-ink-soft bg-rf-cream-alt px-2 py-0.5 rounded-full">{g.kind}</span>
                    {g.example_status != null && (
                      <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${g.example_status >= 500 ? "bg-red-100 text-red-800" : g.example_status >= 400 ? "bg-amber-100 text-amber-800" : "bg-rf-cream-alt text-rf-ink-soft"}`}>
                        {g.example_method ? `${g.example_method} ` : ""}{g.example_status}
                      </span>
                    )}
                    {g.page && <span className="text-[10px] font-mono text-rf-ink-soft/80">{g.page}</span>}
                  </div>
                  <p className="font-mono text-sm text-rf-ink mb-1 break-words">{g.message}</p>
                  {g.example_api_path && (
                    <p className="font-mono text-[11px] text-rf-orange mb-2 break-words">
                      <i className="fa-solid fa-arrow-right-long mr-1" aria-hidden="true" />
                      {g.example_api_path}
                    </p>
                  )}
                  <p className="text-[10px] text-rf-ink-soft/70 uppercase tracking-wider font-bold">
                    First: {new Date(g.first_seen).toLocaleString()} · Last: {new Date(g.last_seen).toLocaleString()}
                  </p>
                  {g.example_stack && (
                    <details className="mt-2">
                      <summary className="text-xs font-bold text-rf-orange cursor-pointer">Stack trace</summary>
                      <pre className="mt-2 text-[10px] bg-rf-ink text-rf-cream p-3 rounded-lg overflow-x-auto max-h-64">{g.example_stack}</pre>
                    </details>
                  )}
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  {g.example_api_path && (
                    <button
                      type="button"
                      data-testid="errors-reproduce-group"
                      onClick={() => startReproduce(g.example_method || "GET", g.example_api_path)}
                      className="bg-rf-ink text-rf-cream !py-1.5 !px-3 !text-xs font-bold rounded-full inline-flex items-center gap-1.5 hover:bg-rf-orange transition-colors"
                    >
                      <i className="fa-solid fa-rotate-right" aria-hidden="true" /> Reproduce
                    </button>
                  )}
                  <button
                    type="button"
                    data-testid="errors-resolve-group"
                    onClick={() => resolveMessage(g.message)}
                    className="btn-outline-ink !py-1.5 !px-3 !text-xs"
                  >
                    Mark {g.count} resolved
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && tab === "recent" && (
        <div className="space-y-2">
          {items.length === 0 && <p className="text-rf-ink-soft italic text-sm">Nothing recent.</p>}
          {items.map((e) => (
            <div key={e.error_id} className="card-recipe !py-3 !px-4" data-testid="errors-row">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <span className="text-[10px] font-black uppercase tracking-wider text-rf-ink-soft">{e.kind}</span>
                    {e.status && <span className="text-[10px] font-mono bg-red-100 text-red-800 px-1.5 py-0.5 rounded">{e.method} {e.status}</span>}
                    {e.page && <span className="text-[10px] font-mono text-rf-ink-soft/80">{e.page}</span>}
                    {e.resolved && <span className="text-[10px] font-black text-emerald-700 uppercase">Resolved</span>}
                  </div>
                  <p className="font-mono text-xs text-rf-ink break-words">{e.message}</p>
                  <p className="text-[10px] text-rf-ink-soft/70 mt-0.5">
                    {new Date(e.created_at).toLocaleString()} · {e.user_agent?.slice(0, 80)}
                  </p>
                </div>
                {!e.resolved && (
                  <button
                    type="button"
                    onClick={() => resolveOne(e.error_id)}
                    className="text-[10px] font-bold text-rf-orange hover:underline shrink-0"
                  >
                    Resolve
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {reproTarget && (
        <div
          className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
          onClick={closeReproduce}
          data-testid="errors-reproduce-modal"
        >
          <div
            className="bg-white rounded-3xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-rf-ink/10 flex items-start justify-between gap-4">
              <div>
                <p className="overline mb-1">Reproduce request</p>
                <p className="font-mono text-sm text-rf-ink break-all">
                  <span className={`font-black uppercase px-2 py-0.5 rounded-full text-xs mr-2 ${reproTarget.method === "GET" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                    {reproTarget.method}
                  </span>
                  {reproTarget.path}
                </p>
              </div>
              <button
                type="button"
                onClick={closeReproduce}
                data-testid="errors-reproduce-close"
                className="text-rf-ink-soft hover:text-rf-ink text-xl leading-none"
                aria-label="Close"
              >×</button>
            </div>
            <div className="p-5 overflow-y-auto flex-1">
              {reproTarget.needsConfirm && !reproResult && !reproLoading && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-4">
                  <p className="font-bold text-amber-900 mb-2">
                    <i className="fa-solid fa-triangle-exclamation mr-2" aria-hidden="true" />
                    This is a write request ({reproTarget.method})
                  </p>
                  <p className="text-sm text-amber-900 mb-3">
                    Replaying it will hit the live database with an empty body.
                    That&apos;s usually safe (most endpoints will reject empty payloads
                    with a 422), but a poorly-guarded endpoint could delete or
                    modify data. Only proceed if you understand the endpoint&apos;s behaviour.
                  </p>
                  <button
                    type="button"
                    data-testid="errors-reproduce-confirm"
                    onClick={() => runReproduce(reproTarget.method, reproTarget.path)}
                    className="btn-amber !py-2 !px-4 !text-sm"
                  >
                    I understand — send the request
                  </button>
                </div>
              )}
              {reproLoading && (
                <div className="flex items-center gap-3 text-rf-ink-soft">
                  <div className="w-4 h-4 border-2 border-rf-orange border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm">Sending request…</span>
                </div>
              )}
              {reproResult && (
                <div>
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <span className={`text-xs font-black uppercase tracking-wider px-2.5 py-1 rounded-full ${
                      reproResult.status === "ERR" ? "bg-red-100 text-red-800"
                      : reproResult.status >= 500 ? "bg-red-100 text-red-800"
                      : reproResult.status >= 400 ? "bg-amber-100 text-amber-800"
                      : "bg-emerald-100 text-emerald-800"
                    }`}>
                      {reproResult.status}
                    </span>
                    <span className="text-xs text-rf-ink-soft font-mono">{reproResult.ms} ms</span>
                    {reproResult.status !== "ERR" && reproResult.status < 400 && (
                      <span className="text-xs text-emerald-700 font-bold">✅ Endpoint healthy — likely a past transient</span>
                    )}
                    {typeof reproResult.status === "number" && reproResult.status >= 500 && (
                      <span className="text-xs text-red-700 font-bold">🔴 Still failing — investigate backend logs</span>
                    )}
                    {typeof reproResult.status === "number" && reproResult.status >= 400 && reproResult.status < 500 && (
                      <span className="text-xs text-amber-800 font-bold">⚠️ Client error — expected for auth/validation</span>
                    )}
                  </div>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <p className="text-xs text-rf-ink-soft font-bold uppercase tracking-wider">Response body</p>
                    <button
                      type="button"
                      data-testid="errors-reproduce-copy-curl"
                      onClick={() => {
                        const token = localStorage.getItem("rf_token") || "";
                        const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
                        const method = reproResult.method.toUpperCase();
                        const url = `${base}/api${reproResult.path.startsWith("/") ? reproResult.path : "/" + reproResult.path}`;
                        const authHeader = token ? ` \\\n  -H "Authorization: Bearer ${token}"` : "";
                        const bodyPart = method === "GET" || method === "HEAD"
                          ? ""
                          : ` \\\n  -H "Content-Type: application/json" \\\n  -d '{}'`;
                        const curl = `curl -X ${method} "${url}"${authHeader}${bodyPart}`;
                        navigator.clipboard.writeText(curl).then(
                          () => toast.success("curl copied to clipboard"),
                          () => toast.error("Copy failed — grab it from the box"),
                        );
                      }}
                      className="text-xs font-bold text-rf-orange hover:text-rf-orange/80 inline-flex items-center gap-1"
                    >
                      <i className="fa-solid fa-clipboard" aria-hidden="true" /> Copy as curl
                    </button>
                  </div>
                  <pre className="text-[11px] bg-rf-ink text-rf-cream p-4 rounded-lg overflow-auto max-h-96 whitespace-pre-wrap break-words">
                    {typeof reproResult.body === "string"
                      ? reproResult.body
                      : JSON.stringify(reproResult.body, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
